import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import VersionPage from '@src/pages/VersionPage'
import WelcomePageBoilerPlate from '@src/pages/WelcomePageBoilerPlate'
import ComponentReview from '@src/pages/component-review'
import UITemplate from '@src/pages/ui-template'
import Dashboard from '@src/pages/Dashboard'
import { DashboardProvider } from '@src/pages/Dashboard/Context'
import ReportEnquiry from '@src/pages/ReportEnquiry'
import { ReportEnquiryProvider } from '@src/pages/ReportEnquiry/Context'
import BulkEnquiry from '@src/pages/BulkEnquiry'
import BulkEnquiryBatch from '@src/pages/BulkEnquiry/BatchDetail'
import { BULK_PERMISSION } from '@src/pages/BulkEnquiry/constants'
import Workforce from '@src/pages/Workforce'
import { WorkforceProvider } from '@src/pages/Workforce/Context'
import Company from '@src/pages/Company'
import CompanyProfile from '@src/pages/Company/CompanyProfile'
import SubscribePage from '@src/pages/Company/SubscribePage'
import UmkmApplication from '@src/pages/Company/UmkmApplication'
import AuditTrail from '@src/pages/AuditTrail'
import { AuditTrailProvider } from '@src/pages/AuditTrail/Context'
import Legal from '@src/pages/Legal'
import ContactUs from '@src/pages/ContactUs'
import EmployeeDetailsSheet from '@src/pages/Workforce/components/EmployeeDetailsSheet'
import RiskSignalsDetailsSheet from '@src/pages/Workforce/components/RiskSignalsDetailsSheet'
import { useEffect, useRef, useState } from 'react'
import { useApp } from '@src/AppContext'
import NotFound from '@src/pages/NotFound'
import { Access, CREDIT_REPORT_PERMISSION, resolveLandingRoute } from '@src/services/Helper'
import { MyButton } from '@interstellar-component'
import Enquiry from '@src/pages/Enquiry'
import { EnquiryProvider } from '@src/pages/Enquiry/Context'
import Login from '@src/pages/Login'
import { LoginProvider } from '@src/pages/Login/Context'
import Register from '@src/pages/Register'
import { RegisterProvider } from '@src/pages/Register/Context'
import MobileSignature from '@src/pages/MobileSignature'
import Profile from '@src/pages/Profile'
import { ProfileProvider } from '@src/pages/Profile/context'
import ForgetPassword from '@src/pages/Forget-password'
import { ForgetPasswordProvider } from '@src/pages/Forget-password/context'
import RegisterCompanyInfo from '@src/pages/RegisterCompanyInfo'
import { RegisterCompanyInfoProvider } from '@src/pages/RegisterCompanyInfo/Context'
import SigningComplete from '@src/pages/RegisterCompanyInfo/SigningComplete'

import Settings from '@src/pages/Settings'
import GeneralSettings from '@src/pages/Settings/GeneralSettings/GeneralSettings'
import UserRoleAccess from '@src/pages/Settings/UserRoleAccess/UserRoleAccess'
import UserTab from '@src/pages/Settings/UserRoleAccess/User/UserTab'
import RoleTab from '@src/pages/Settings/UserRoleAccess/Role/RoleTab'
import EmploymentLevel from '@src/pages/Settings/EmploymentSettings/EmploymentLevel'
import ConsentEditor from '@src/pages/Settings/GeneralSettings/ConsentEditor'
import GetContactSettings from '@src/pages/Settings/GetContactSettings/GetContactSettings'
import StepEmailVerified from '@src/pages/Register/components/StepEmailVerified'
import PaymentResult from '@src/pages/Billing/PaymentResult'

function PermGated({ moduleKey, subPermission = null, fallback, children }) {
  const { hasPermission, permissionsLoaded } = useApp()
  if (!permissionsLoaded) return null
  if (!hasPermission(moduleKey, subPermission)) return fallback
  return children
}

// Risk Signals is built from the credit report snapshot — the BE 403s it
// without view_credit_report, so a direct URL falls back to the employee sheet.
function RiskSignalsGated() {
  const [moduleKey, subPermission] = CREDIT_REPORT_PERMISSION
  return (
    <PermGated moduleKey={moduleKey} subPermission={subPermission} fallback={<Navigate to=".." replace />}>
      <RiskSignalsDetailsSheet />
    </PermGated>
  )
}

function FullPageSpinner() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand/300 border-t-brand/900" />
    </div>
  )
}

function NoAccessLanding() {
  const { logout } = useApp()
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-2xl font-semibold text-gray-900">No access</h1>
      <p className="max-w-md text-md-regular text-gray-600">
        Your account doesn’t have access to any module yet. Please contact your
        administrator to request access.
      </p>
      <MyButton variant="filled" color="primary" size="md" onClick={logout}>
        Sign out
      </MyButton>
    </div>
  )
}

// Resolves where an authenticated user should land. Waits for the /auth/session
// response so permission checks are not made against an empty list, then routes
// to the first sidebar menu the user can access. Falls back to the No-Access
// page when no module is permitted at all.
function LandingRedirect() {
  const { hasPermission, permissionsLoaded } = useApp()
  if (!permissionsLoaded) return <FullPageSpinner />
  const target = resolveLandingRoute(hasPermission)
  if (!target) return <NoAccessLanding />
  return <Navigate to={target} replace />
}

export function AuthenticatedRoutes() {
  const { hasPermission, permissionsLoaded } = useApp()
  const location = useLocation()
  const previousLocationRaw = useRef(location)
  const [backgroundLocation, setBackgroundLocation] = useState(null)

  const isStackedRoute = (path) => path.startsWith('/workforce/employee')

  useEffect(() => {
    if (isStackedRoute(location.pathname)) {
      if (!isStackedRoute(previousLocationRaw.current.pathname)) {
        setBackgroundLocation(previousLocationRaw.current)
      }
    } else {
      setBackgroundLocation(null)
    }
    previousLocationRaw.current = location
  }, [location])

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<LandingRedirect />} />
        <Route
          path="/dashboard"
          element={
            <PermGated moduleKey={Access.DASHBOARD} fallback={<LandingRedirect />}>
              <DashboardProvider>
                <Dashboard />
              </DashboardProvider>
            </PermGated>
          }
        />
        <Route
          path="/report-enquiry"
          element={
            <ReportEnquiryProvider>
              <ReportEnquiry />
            </ReportEnquiryProvider>
          }
        />
        <Route
          path="/bulk-enquiry"
          element={
            <PermGated moduleKey={Access.ENQUIRY} subPermission={BULK_PERMISSION} fallback={<NotFound />}>
              <BulkEnquiry />
            </PermGated>
          }
        />
        <Route
          path="/bulk-enquiry/:batchId"
          element={
            <PermGated moduleKey={Access.ENQUIRY} subPermission={BULK_PERMISSION} fallback={<NotFound />}>
              <BulkEnquiryBatch />
            </PermGated>
          }
        />
        <Route
          path="/workforce"
          element={
            <WorkforceProvider>
              <Workforce />
            </WorkforceProvider>
          }
        >
          <Route path="employee/:id" element={<EmployeeDetailsSheet />}>
            <Route path="risk-signals" element={<RiskSignalsGated />} />
          </Route>
        </Route>
        <Route path="/company" element={<Company />} />
        <Route path="/company/profile" element={<CompanyProfile />} />
        <Route path="/company/:companyId/subscribe" element={<SubscribePage />} />
        <Route path="/company/:companyId/umkm" element={<UmkmApplication />} />
        <Route
          path="/audit-trail"
          element={
            <AuditTrailProvider>
              <AuditTrail />
            </AuditTrailProvider>
          }
        />
        <Route path="/legal" element={<Legal />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/billing/success" element={<PaymentResult variant="success" />} />
        <Route path="/billing/failure" element={<PaymentResult variant="failure" />} />

        <Route path="/settings" element={<Settings />}>
          <Route index element={<Navigate to="general" replace />} />
          <Route path="general" element={<GeneralSettings />} />
          <Route path="user-role-access" element={<UserRoleAccess />}>
            <Route index element={<Navigate to="user" replace />} />
            <Route
              path="user"
              element={
                <PermGated
                  moduleKey={Access.USER_MANAGEMENT}
                  fallback={<Navigate to="/settings/user-role-access/role" replace />}
                >
                  <UserTab />
                </PermGated>
              }
            />
            <Route
              path="role"
              element={
                <PermGated
                  moduleKey={Access.ROLE_ACCESS}
                  fallback={<Navigate to="/settings/user-role-access/user" replace />}
                >
                  <RoleTab />
                </PermGated>
              }
            />
          </Route>
          <Route path="employment-level" element={<EmploymentLevel />} />
          <Route
            path="consent-editor"
            element={
              <PermGated moduleKey={Access.CONSENT_EDITOR} fallback={<Navigate to="/settings/general" replace />}>
                <ConsentEditor />
              </PermGated>
            }
          />
          <Route
            path="getcontact"
            element={
              <PermGated moduleKey={Access.GETCONTACT_SETTING} fallback={<Navigate to="/settings/general" replace />}>
                <GetContactSettings />
              </PermGated>
            }
          />
        </Route>

        <Route path="/login" element={<Navigate to="/" replace />} />
        {/* Auth-only routes: when already authenticated, redirect to landing
            instead of hitting the catch-all /404. Without these, the brief
            route swap after forgot-password auto-login (cookie set while URL is
            still /forget-password) flashes /404 before the hard reload. */}
        <Route path="/forget-password" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route
          path="/profile"
          element={
            <ProfileProvider>
              <Profile />
            </ProfileProvider>
          }
        />
        <Route
          path="/enquiry"
          element={
            <EnquiryProvider>
              {!permissionsLoaded ? null : hasPermission(Access.ENQUIRY) ? (
                <Enquiry />
              ) : (
                <NotFound />
              )}
            </EnquiryProvider>
          }
        />
        <Route
          path="/register-company-info"
          element={
            <RegisterCompanyInfoProvider>
              <RegisterCompanyInfo />
            </RegisterCompanyInfoProvider>
          }
        />
        <Route
          path="/company/:id/enrollment/signing-complete"
          element={<SigningComplete />}
        />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>

      {backgroundLocation && isStackedRoute(location.pathname) && (
        <Routes location={location}>
          <Route path="/settings" element={<Settings />}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="user-role-access" element={<UserRoleAccess />}>
              <Route index element={<Navigate to="user" replace />} />
              <Route
                path="user"
                element={
                  <PermGated
                    moduleKey={Access.USER_MANAGEMENT}
                    fallback={<Navigate to="/settings/user-role-access/role" replace />}
                  >
                    <UserTab />
                  </PermGated>
                }
              />
              <Route
                path="role"
                element={
                  <PermGated
                    moduleKey={Access.ROLE_ACCESS}
                    fallback={<Navigate to="/settings/user-role-access/user" replace />}
                  >
                    <RoleTab />
                  </PermGated>
                }
              />
            </Route>
            <Route path="employment-level" element={<EmploymentLevel />} />
            <Route
              path="consent-editor"
              element={
                <PermGated moduleKey={Access.CONSENT_EDITOR} fallback={<Navigate to="/settings/general" replace />}>
                  <ConsentEditor />
                </PermGated>
              }
            />
            <Route
              path="getcontact"
              element={
                <PermGated moduleKey={Access.GETCONTACT_SETTING} fallback={<Navigate to="/settings/general" replace />}>
                  <GetContactSettings />
                </PermGated>
              }
            />
          </Route>
          <Route
            path="/workforce/employee/:id"
            element={
              <WorkforceProvider>
                <EmployeeDetailsSheet />
              </WorkforceProvider>
            }
          >
            <Route path="risk-signals" element={<RiskSignalsGated />} />
          </Route>
        </Routes>
      )}
    </>
  )
}

export function UnauthenticatedRoutes() {
  return (
    <Routes>
      <Route path="*" element={<Navigate to="/login" replace />} />
      <Route
        path="/login"
        element={
          <LoginProvider>
            <Login />
          </LoginProvider>
        }
      />
      <Route
        path="/register"
        element={
          <RegisterProvider>
            <Register />
          </RegisterProvider>
        }
      />
      <Route
        path="/forget-password"
        element={
          <ForgetPasswordProvider>
            <ForgetPassword />
          </ForgetPasswordProvider>
        }
      />
      <Route path="/email-verified" element={<StepEmailVerified />} />
      <Route path="/welcome" element={<WelcomePageBoilerPlate />} />
      <Route path="/version" element={<VersionPage />} />
      <Route path="/component-review" element={<ComponentReview />} />
      <Route path="/ui-template" element={<UITemplate />} />
      <Route path="/mobile-signature/:roomId" element={<MobileSignature />} />
    </Routes>
  )
}
